import { jsx, jsxs } from "react/jsx-runtime";
import { Typography, useTheme, useThemeProps } from "@mui/material";
import { forwardRef } from "react";
import { useSlotProps } from "../../base/index.js";
import { CheckCircle, Error as ErrorIcon, Info, Warning } from "../../icons/index.js";
import { chipName } from "./classes.js";
import { Content } from "./components/Content.js";
import { RightAdornmentPlacer } from "./components/RightAdornmentPlacer.js";
import { Root } from "./components/Root.js";
import { useUtilityClasses } from "./hooks/useUtilityClasses.js";
const Chip = forwardRef(function Chip2(inProps, ref) {
  const props = useThemeProps({
    name: chipName,
    props: inProps
  });
  const {
    sx,
    size = "medium",
    label,
    color,
    testId = `${chipName}-root`,
    className = "",
    components,
    componentsProps,
    rightAdornment = null,
    icon: iconProp,
    ...rest
  } = props;
  const icon = props.avatar ? void 0 : iconProp ?? getAutomaticIcon(props.color);
  const theme = useTheme();
  const ownerState = props;
  const utilityClasses = useUtilityClasses(ownerState);
  const shiftX = !props.onDelete;
  const badgeBackgroundColor = getBadgeBackgroundColor(props.variant, props.disabled, theme);
  const chipTextColor = props.disabled ? (theme.vars ?? theme).palette.text.disabled : (theme.vars ?? theme).palette.text.primary;
  const rootSlotProps = useSlotProps({
    className: `${className} ${utilityClasses.root}`,
    ownerState,
    elementType: Root,
    additionalProps: {
      sx,
      ref,
      icon,
      size,
      color,
      style: {
        "--badge-background-color": badgeBackgroundColor,
        "--badge-text-color": chipTextColor,
        "--badge-size": size === "medium" ? "20px" : "18px"
      },
      "data-tidal": chipName,
      "data-testid": testId
    },
    externalSlotProps: Object.assign(rest, componentsProps?.root)
  });
  const contentSlotProps = useSlotProps({
    className: utilityClasses.content,
    ownerState,
    elementType: Content,
    additionalProps: {
      as: components?.Content
    },
    externalSlotProps: componentsProps?.content
  });
  return /* @__PURE__ */ jsx(
    Root,
    {
      ...rootSlotProps,
      label: /* @__PURE__ */ jsxs(Content, { ...contentSlotProps, children: [
        /* @__PURE__ */ jsx(Typography, { noWrap: true, fontSize: "inherit", children: label }),
        rightAdornment && /* @__PURE__ */ jsx(
          RightAdornmentPlacer,
          {
            className: utilityClasses.rightAdornmentPlacer,
            ownerState,
            shiftX,
            children: rightAdornment
          }
        )
      ] })
    }
  );
});
function getAutomaticIcon(color) {
  switch (color) {
    case "success":
      return /* @__PURE__ */ jsx(CheckCircle, {});
    case "error":
      return /* @__PURE__ */ jsx(ErrorIcon, {});
    case "warning":
      return /* @__PURE__ */ jsx(Warning, {});
    case "info":
      return /* @__PURE__ */ jsx(Info, {});
  }
}
function getBadgeBackgroundColor(variant, disabled, theme) {
  if (disabled) return (theme.vars ?? theme).palette.action.disabledBackground;
  switch (variant) {
    case "filled":
    case "filledOutline":
      return (theme.vars ?? theme).palette.common.white;
    case "outlined":
      return (theme.vars ?? theme).palette.tidalColors.slateBlueGray[100];
  }
  return "";
}
export {
  Chip
};
//# sourceMappingURL=Chip.js.map
